import React, { useRef, useState, useEffect } from "react";
import { X, MessageCircle, User, Send, Trash2, Calendar } from "lucide-react";
import { cn } from "../../lib/utils";
import { Button } from "./button";
import { aiChatService } from "../../services/aiChatService";
import { db } from "../../firebase";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { useAuth } from "../../hooks/useAuth";

const chatConfig = {
  dimensions: {
    sm: "sm:max-w-sm sm:max-h-[500px]",
    md: "sm:max-w-md sm:max-h-[600px]",
    lg: "sm:max-w-lg sm:max-h-[700px]",
    xl: "sm:max-w-xl sm:max-h-[800px]",
    full: "sm:w-full sm:h-full",
  },
  positions: {
    "bottom-right": "bottom-6 right-6",
    "bottom-left": "bottom-6 left-6",
  },
  chatPositions: {
    "bottom-right": "sm:bottom-[calc(100%+12px)] sm:right-0",
    "bottom-left": "sm:bottom-[calc(100%+12px)] sm:left-0",
  },
  states: {
    open: "pointer-events-auto opacity-100 visible scale-100 translate-y-0",
    closed: "pointer-events-none opacity-0 invisible scale-95 sm:translate-y-3",
  },
};

const ExpandableChat = ({
  className,
  position = "bottom-right",
  size = "md",
  icon,
  children,
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const chatRef = useRef(null);

  const toggleChat = () => setIsOpen(!isOpen);

  return (
    <div
      className={cn(`fixed ${chatConfig.positions[position]} z-50`, className)}
      {...props}
    >
      <div
        ref={chatRef}
        className={cn(
          "flex flex-col bg-gradient-to-br from-black/90 via-black/80 to-black/90 backdrop-blur-lg border border-yellow-500/50 sm:rounded-2xl shadow-2xl overflow-hidden transition-all duration-300 ease-out sm:absolute sm:w-[90vw] sm:h-[80vh] fixed inset-0 w-full h-full sm:inset-auto relative",
          chatConfig.chatPositions[position],
          chatConfig.dimensions[size],
          isOpen ? chatConfig.states.open : chatConfig.states.closed,
          className,
        )}
      >
        {/* Animated gradient background */}
        <div className="absolute inset-0 bg-gradient-to-r from-yellow-400/5 via-transparent to-yellow-400/5 animate-[gradientShift_4s_ease-in-out_infinite] opacity-50" />
        
        {children}
        <Button
          variant="ghost"
          size="icon"
          className="absolute top-2 right-2 sm:hidden text-white/80 hover:text-white hover:bg-black/30"
          onClick={toggleChat}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
      <ExpandableChatToggle
        icon={icon}
        isOpen={isOpen}
        toggleChat={toggleChat}
      />
    </div>
  );
};

const ExpandableChatHeader = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex items-center justify-between p-6 border-b border-yellow-400/30 relative z-10", className)}
    {...props}
  />
);

const ExpandableChatBody = ({
  className,
  ...props
}) => (
  <div 
    className={cn("flex-grow overflow-y-auto p-6 relative z-10", className)} 
    {...props} 
  />
);

const ExpandableChatFooter = ({
  className,
  ...props
}) => (
  <div 
    className={cn("border-t border-yellow-400/30 p-6 relative z-10", className)} 
    {...props} 
  />
);

const ExpandableChatToggle = ({
  className,
  icon,
  isOpen,
  toggleChat,
  ...props
}) => (
  <Button
    onClick={toggleChat}
    className={cn(
      "w-14 h-14 rounded-full bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-500 hover:to-yellow-600 text-black shadow-lg hover:shadow-xl hover:shadow-yellow-500/25 transition-all duration-300 flex items-center justify-center border border-yellow-300/20 hover:scale-110",
      className,
    )}
    {...props}
  >
    {isOpen ? (
      <X className="h-6 w-6 transition-transform duration-200" />
    ) : (
      icon || <User className="h-6 w-6 transition-transform duration-200" />
    )}
  </Button>
);

// Functional AI Chat Component
const FunctionalChat = ({ currentPage = '/' }) => {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showBookingButton, setShowBookingButton] = useState(false);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const messagesEndRef = useRef(null);
  const { user } = useAuth();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Update page context whenever it changes
    aiChatService.setCurrentPage(currentPage);
  }, [currentPage]);

  useEffect(() => {
    // Load saved conversation on mount
    const history = aiChatService.getConversationHistory();
    if (history.length > 0) {
      // Convert history to messages format
      const loadedMessages = history.map((msg) => ({
        role: msg.role,
        content: msg.content,
        timestamp: new Date() // We don't save timestamps, use current date
      }));
      setMessages(loadedMessages);
    } else {
      // Welcome message on first load
      setMessages([{
        role: 'assistant',
        content: "Hey there! I'm your AI assistant. I'm here to fire you up and help you crush any doubts or questions you got. What's on your mind? 🚀",
        timestamp: new Date()
      }]);
    }
  }, []);

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = inputValue.trim();
    setInputValue('');
    setError(null);

    // Add user message to UI
    const newUserMessage = {
      role: 'user',
      content: userMessage,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, newUserMessage]);
    setIsLoading(true);

    try {
      // Send to AI service
      const response = await aiChatService.sendMessage(userMessage);

      if (response.success) {
        // Add AI response to UI
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: response.message,
          timestamp: new Date()
        }]);
        
        // Auto-open booking modal if user explicitly asks for appointment
        const bookingKeywords = ['book', 'appointment', 'schedule', 'meeting', 'call me', 'talk to someone', 'speak with', 'help'];
        const asksForBooking = bookingKeywords.some(keyword => 
          userMessage.toLowerCase().includes(keyword)
        );
        
        // Show button immediately if user asks for booking OR after 3 exchanges
        if (asksForBooking || response.shouldShowBooking || messages.length >= 4) {
          setShowBookingButton(true);
          
          // Auto-open modal if explicitly asking for appointment
          if (asksForBooking) {
            setTimeout(() => setShowBookingModal(true), 800);
          }
        }
      } else {
        setError(response.error);
        // Still show the fallback message
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: response.message,
          timestamp: new Date(),
          isError: true
        }]);
      }
    } catch (err) {
      setError(err.message);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "Whoa, something went wrong there! But don't worry, we got this. Try again? 💪",
        timestamp: new Date(),
        isError: true
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClearChat = () => {
    if (window.confirm('Clear all messages and start fresh?')) {
      aiChatService.clearConversation();
      setMessages([{
        role: 'assistant',
        content: "Alright, fresh start! I'm pumped. What do you want to talk about? 🔥",
        timestamp: new Date()
      }]);
      setError(null);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <>
      <ExpandableChatHeader className="flex-row items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-yellow-400 to-yellow-500 rounded-lg flex items-center justify-center shadow-lg">
            <User className="h-5 w-5 text-black" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-yellow-400">Personal Assistant</h1>
            <p className="text-xs text-white/70">
              {isLoading ? 'Thinking...' : 'Ready to help'}
            </p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={handleClearChat}
          className="text-white/60 hover:text-white hover:bg-white/10"
          title="Clear conversation"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </ExpandableChatHeader>

      <ExpandableChatBody className="flex flex-col gap-4 overflow-y-auto">
        {/* Error Banner */}
        {error && (
          <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-3 text-sm text-red-200">
            <p className="font-semibold">Note:</p>
            <p className="text-xs mt-1">{error}</p>
          </div>
        )}

        {/* Messages */}
        <div className="space-y-4">
          {messages.map((message, index) => (
            <div
              key={index}
              className={cn(
                "flex gap-3",
                message.role === 'user' ? 'flex-row-reverse' : 'flex-row'
              )}
            >
              <div className={cn(
                "w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0",
                message.role === 'user'
                  ? 'bg-gradient-to-br from-blue-400 to-blue-600'
                  : 'bg-gradient-to-br from-yellow-400 to-yellow-500'
              )}>
                {message.role === 'user' ? (
                  <MessageCircle className="h-4 w-4 text-white" />
                ) : (
                  <User className="h-4 w-4 text-black" />
                )}
              </div>
              <div className={cn(
                "flex-1 rounded-2xl px-4 py-3 max-w-[80%]",
                message.role === 'user'
                  ? 'bg-blue-600/30 border border-blue-500/30'
                  : message.isError
                  ? 'bg-yellow-600/20 border border-yellow-500/30'
                  : 'bg-white/10 border border-white/20'
              )}>
                <p className="text-white text-sm leading-relaxed whitespace-pre-wrap">
                  {message.content}
                </p>
                <p className="text-white/40 text-xs mt-2">
                  {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
            </div>
          ))}

          {isLoading && (
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-yellow-400 to-yellow-500 flex items-center justify-center">
                <User className="h-4 w-4 text-black animate-pulse" />
              </div>
              <div className="bg-white/10 border border-white/20 rounded-2xl px-4 py-3">
                <div className="flex gap-1">
                  <div className="w-2 h-2 bg-yellow-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                  <div className="w-2 h-2 bg-yellow-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                  <div className="w-2 h-2 bg-yellow-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </ExpandableChatBody>

      <ExpandableChatFooter>
        {/* Book Appointment Button - Appears after 3 exchanges */}
        {showBookingButton && (
          <div className="mb-3 flex justify-center">
            <Button
              onClick={() => setShowBookingModal(true)}
              className="bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-500 hover:to-yellow-600 text-black font-semibold px-6 py-2 rounded-xl shadow-lg hover:shadow-yellow-500/25 transition-all duration-200 flex items-center gap-2"
            >
              <Calendar className="h-4 w-4" />
              Book an Appointment
            </Button>
          </div>
        )}
        
        <div className="flex gap-2">
          <div className="flex-1 relative rounded-xl border border-yellow-400/30 bg-black/30 backdrop-blur-sm overflow-hidden">
            <textarea
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ask me anything..."
              className="w-full bg-transparent text-white placeholder-white/50 outline-none p-3 pr-12 text-sm resize-none"
              rows="1"
              style={{
                minHeight: '44px',
                maxHeight: '120px'
              }}
              disabled={isLoading}
            />
          </div>
          <Button
            onClick={handleSendMessage}
            disabled={!inputValue.trim() || isLoading}
            className={cn(
              "h-11 w-11 rounded-xl transition-all duration-200",
              inputValue.trim() && !isLoading
                ? "bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-500 hover:to-yellow-600 text-black shadow-lg hover:shadow-yellow-500/25"
                : "bg-white/10 text-white/30 cursor-not-allowed"
            )}
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
        <div className="text-center mt-3">
          <p className="text-xs text-white/50">
            Here to motivate and inspire you
          </p>
        </div>
      </ExpandableChatFooter>
      
      {/* Appointment Booking Modal */}
      {showBookingModal && (
        <AppointmentBookingModal
          isOpen={showBookingModal}
          onClose={() => setShowBookingModal(false)}
          user={user}
        />
      )}
    </>
  );
};

// Appointment Booking Modal Component
const AppointmentBookingModal = ({ isOpen, onClose, user }) => {
  const [appointmentData, setAppointmentData] = useState({
    title: 'Consultation Call',
    date: '',
    time: '',
    duration: '30',
    notes: '',
    type: 'consultation'
  });
  const [isSaving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      // Combine date and time
      const appointmentDateTime = new Date(`${appointmentData.date}T${appointmentData.time}`);
      
      // Save to Firebase
      await addDoc(collection(db, 'appointments'), {
        userId: user?.uid || 'anonymous',
        userEmail: user?.email || 'No email',
        userName: user?.displayName || 'Guest',
        title: appointmentData.title,
        dateTime: appointmentDateTime,
        duration: parseInt(appointmentData.duration),
        notes: appointmentData.notes,
        type: appointmentData.type,
        status: 'pending',
        source: 'AI Chat',
        createdAt: serverTimestamp()
      });

      setSuccess(true);
      
      // Close after 2 seconds
      setTimeout(() => {
        onClose();
        setSuccess(false);
      }, 2000);
      
    } catch (error) {
      console.error('Error saving appointment:', error);
      alert('Failed to book appointment. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={(e) => {
      // Close modal if clicking outside
      if (e.target === e.currentTarget) onClose();
    }}>
      <div className="bg-gradient-to-br from-black/90 via-black/80 to-black/90 border border-yellow-400/50 rounded-2xl shadow-2xl w-full max-w-md max-h-[85vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-yellow-400/30 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-yellow-400 to-yellow-500 rounded-lg flex items-center justify-center">
              <Calendar className="h-5 w-5 text-black" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-yellow-400">Book Appointment</h2>
              <p className="text-sm text-white/70">Let's schedule a time to chat!</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="text-white/60 hover:text-white hover:bg-white/10"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Body - Scrollable */}
        <div className="p-6 overflow-y-auto flex-1">
          {success ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-green-500/20 border-2 border-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Appointment Booked! 🎉</h3>
              <p className="text-white/70">We'll reach out to confirm shortly.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Appointment Type */}
              <div>
                <label className="block text-sm font-medium text-white/90 mb-2">
                  Appointment Type
                </label>
                <select
                  value={appointmentData.type}
                  onChange={(e) => setAppointmentData({ ...appointmentData, type: e.target.value })}
                  className="w-full bg-black/30 border border-yellow-400/30 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-yellow-400/60"
                  required
                >
                  <option value="consultation">Initial Consultation</option>
                  <option value="follow-up">Follow-up Call</option>
                  <option value="training">Training Session</option>
                  <option value="onboarding">Onboarding</option>
                </select>
              </div>

              {/* Date */}
              <div>
                <label className="block text-sm font-medium text-white/90 mb-2">
                  Date
                </label>
                <input
                  type="date"
                  value={appointmentData.date}
                  onChange={(e) => setAppointmentData({ ...appointmentData, date: e.target.value })}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full bg-black/30 border border-yellow-400/30 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-yellow-400/60"
                  required
                />
              </div>

              {/* Time */}
              <div>
                <label className="block text-sm font-medium text-white/90 mb-2">
                  Time
                </label>
                <input
                  type="time"
                  value={appointmentData.time}
                  onChange={(e) => setAppointmentData({ ...appointmentData, time: e.target.value })}
                  className="w-full bg-black/30 border border-yellow-400/30 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-yellow-400/60"
                  required
                />
              </div>

              {/* Duration */}
              <div>
                <label className="block text-sm font-medium text-white/90 mb-2">
                  Duration (minutes)
                </label>
                <select
                  value={appointmentData.duration}
                  onChange={(e) => setAppointmentData({ ...appointmentData, duration: e.target.value })}
                  className="w-full bg-black/30 border border-yellow-400/30 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-yellow-400/60"
                  required
                >
                  <option value="15">15 minutes</option>
                  <option value="30">30 minutes</option>
                  <option value="45">45 minutes</option>
                  <option value="60">1 hour</option>
                </select>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-white/90 mb-2">
                  Notes (Optional)
                </label>
                <textarea
                  value={appointmentData.notes}
                  onChange={(e) => setAppointmentData({ ...appointmentData, notes: e.target.value })}
                  placeholder="Any specific topics you'd like to discuss?"
                  className="w-full bg-black/30 border border-yellow-400/30 rounded-lg px-4 py-2 text-white placeholder-white/50 focus:outline-none focus:border-yellow-400/60 resize-none"
                  rows="3"
                />
              </div>

              {/* Submit Button */}
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  onClick={onClose}
                  className="flex-1 bg-white/10 hover:bg-white/20 text-white border border-white/20"
                  disabled={isSaving}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-500 hover:to-yellow-600 text-black font-semibold"
                  disabled={isSaving}
                >
                  {isSaving ? 'Booking...' : 'Confirm Booking'}
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

// Coming Soon Chat Content Component (keeping for backwards compatibility)
const ComingSoonChat = ({ currentPage = '/' }) => {
  // Now just redirects to functional chat
  return <FunctionalChat currentPage={currentPage} />;
};

ExpandableChat.displayName = "ExpandableChat";
ExpandableChatHeader.displayName = "ExpandableChatHeader";
ExpandableChatBody.displayName = "ExpandableChatBody";
ExpandableChatFooter.displayName = "ExpandableChatFooter";
ExpandableChatToggle.displayName = "ExpandableChatToggle";

export {
  ExpandableChat,
  ExpandableChatHeader,
  ExpandableChatBody,
  ExpandableChatFooter,
  ComingSoonChat,
  FunctionalChat,
};